/**
 * <login-drawer>: phone OTP login for the live site, Shopify only.
 *
 * Every step is one POST to the Supabase function live-login (supabase/functions/live-login):
 *   - request       (send the SMS code; fire-and-forget on phone submit + resend)
 *   - verify        (check the code -> token + the Shopify customers with this phone)
 *   - complete      (log in as one of them)
 *   - email         (the customer typed an email: free or taken)
 *   - create        (new Shopify customer with this phone, when the email is free)
 *   - email-send    (a code to the email of the account that already holds it)
 *   - email-verify  (that code -> the phone moves to that account, log in)
 *
 * Security:
 *   - verification_token lives ONLY on the instance (this._verificationToken).
 *   - Never persisted to localStorage/sessionStorage.
 *   - Cleared on _resetState() (called from open()).
 *   - Customer list rendering uses createElement + textContent (no innerHTML).
 */

const SUPABASE_FUNCTIONS_BASE = 'https://rzoohllcsoctvowchivq.supabase.co/functions/v1';
const LOGIN_BASE = SUPABASE_FUNCTIONS_BASE + '/live-login';
const ENDPOINTS = {
  request:        LOGIN_BASE + '/request',
  verify:         LOGIN_BASE + '/verify',
  complete:       LOGIN_BASE + '/complete',
  submitEmail:    LOGIN_BASE + '/email',
  createCustomer: LOGIN_BASE + '/create',
  sendEmailOtp:   LOGIN_BASE + '/email-send',
  verifyEmailOtp: LOGIN_BASE + '/email-verify',
};

const SUCCESS_REDIRECT_DELAY_MS = 1500;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

class LoginDrawer extends HTMLElement {
  constructor() {
    super();
    this._step = 'phone';
    this._phone = '';
    this._verificationToken = '';
    this._customers = [];
    this._resendCooldownInterval = null;
    this._resendCooldownSeconds = 0;

    // Phase 3 state
    this._email = '';
    this._existingPhoneMasked = '';
    this._emailScreenOrigin = 'phone';   // 'phone' | 'customers': for "back" from email
    this._emailResendCooldownInterval = null;
    this._emailResendCooldownSeconds = 0;
  }

  connectedCallback() {
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    const overlay = this.querySelector('#LoginDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));

    this._wirePhoneForm();
    this._wireOtpForm();
    this._wireBackButton();
    this._wireResendOtp();
    this._wireCustomerSelection();

    // Phase 3
    this._wireNoneOfThese();
    this._wireEmailForm();
    this._wireRegisterForm();
    this._wireEmailConflict();
    this._wireEmailOtpForm();
    this._wireEmailOtpResend();
    this._wireExtraBackButtons();
  }

  // ---------- Open / Close ----------

  open(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy;
    this._resetState();
    setTimeout(() => this.classList.add('animate', 'active'));
    this.addEventListener(
      'transitionend',
      () => {
        const focusElement = this.querySelector('#LoginDrawer-Phone') || this.querySelector('.drawer__inner');
        if (typeof trapFocus === 'function') {
          trapFocus(this.querySelector('.drawer__inner'), focusElement);
        } else if (focusElement) {
          focusElement.focus();
        }
      },
      { once: true }
    );
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this._stopResendCooldown();
    this._stopEmailResendCooldown();
    this.classList.remove('active');
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }
    document.body.classList.remove('overflow-hidden');
  }

  // ---------- Resend cooldown (phone OTP) ----------

  _startResendCooldown(seconds = 30) {
    this._stopResendCooldown();
    this._resendCooldownSeconds = seconds;
    this._updateResendCooldownUI(this._resendCooldownSeconds);
    this._resendCooldownInterval = setInterval(() => {
      this._resendCooldownSeconds -= 1;
      if (this._resendCooldownSeconds <= 0) {
        this._stopResendCooldown();
      } else {
        this._updateResendCooldownUI(this._resendCooldownSeconds);
      }
    }, 1000);
  }

  _stopResendCooldown() {
    if (this._resendCooldownInterval) {
      clearInterval(this._resendCooldownInterval);
      this._resendCooldownInterval = null;
    }
    this._resendCooldownSeconds = 0;
    this._updateResendCooldownUI(0);
  }

  _updateResendCooldownUI(secondsLeft) {
    const btn = this.querySelector('[data-action="resend-otp"]');
    if (!btn) return;
    if (secondsLeft > 0) {
      btn.disabled = true;
      btn.textContent = `שלח שוב בעוד ${secondsLeft} שניות`;
    } else {
      btn.disabled = false;
      btn.textContent = 'לא קיבלת SMS? שלח בשנית';
    }
  }

  // ---------- Resend cooldown (email OTP) ----------

  _startEmailResendCooldown(seconds = 30) {
    this._stopEmailResendCooldown();
    this._emailResendCooldownSeconds = seconds;
    this._updateEmailResendCooldownUI(this._emailResendCooldownSeconds);
    this._emailResendCooldownInterval = setInterval(() => {
      this._emailResendCooldownSeconds -= 1;
      if (this._emailResendCooldownSeconds <= 0) {
        this._stopEmailResendCooldown();
      } else {
        this._updateEmailResendCooldownUI(this._emailResendCooldownSeconds);
      }
    }, 1000);
  }

  _stopEmailResendCooldown() {
    if (this._emailResendCooldownInterval) {
      clearInterval(this._emailResendCooldownInterval);
      this._emailResendCooldownInterval = null;
    }
    this._emailResendCooldownSeconds = 0;
    this._updateEmailResendCooldownUI(0);
  }

  _updateEmailResendCooldownUI(secondsLeft) {
    const btn = this.querySelector('[data-action="resend-email-otp"]');
    if (!btn) return;
    if (secondsLeft > 0) {
      btn.disabled = true;
      btn.textContent = `שלח שוב בעוד ${secondsLeft} שניות`;
    } else {
      btn.disabled = false;
      btn.textContent = 'שלח שוב';
    }
  }

  // ---------- Step management ----------

  showStep(step) {
    this._step = step;
    this.dataset.step = step;

    const heading = this.querySelector('#LoginDrawer-Heading');
    if (heading) {
      const next = heading.getAttribute(`data-text-${step}`);
      if (next) heading.textContent = next;
    }

    this.querySelectorAll('[data-screen]').forEach((el) => {
      if (el.getAttribute('data-screen') === step) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    });

    // Focus first input on entering screens that have one
    const autoFocusByStep = {
      phone: '#LoginDrawer-Phone',
      otp: '#LoginDrawer-Otp',
      customers: '[data-customers-list] [data-customer-id]',
      email: '#LoginDrawer-Email',
      register: '#LoginDrawer-FirstName',
      'email-otp': '#LoginDrawer-EmailOtp',
    };
    const sel = autoFocusByStep[step];
    if (sel) {
      const target = this.querySelector(sel);
      if (target) setTimeout(() => target.focus(), 50);
    }
  }

  _resetState() {
    this._stopResendCooldown();
    this._stopEmailResendCooldown();

    this._phone = '';
    this._verificationToken = '';
    this._customers = [];
    this._email = '';
    this._existingPhoneMasked = '';
    this._emailScreenOrigin = 'phone';

    const phoneInput     = this.querySelector('#LoginDrawer-Phone');
    const phoneSubmit    = this.querySelector('[data-action="submit-phone"]');
    const phoneError     = this.querySelector('#LoginDrawer-PhoneError');
    const otpInput       = this.querySelector('#LoginDrawer-Otp');
    const otpSubmit      = this.querySelector('[data-action="submit-otp"]');
    const otpError       = this.querySelector('#LoginDrawer-OtpError');
    const resendBtn      = this.querySelector('[data-action="resend-otp"]');
    const resendStatus   = this.querySelector('[data-resend-status]');
    const customersList  = this.querySelector('[data-customers-list]');
    const customersError = this.querySelector('[data-customers-error]');

    if (phoneInput)  phoneInput.value = '';
    if (phoneSubmit) { phoneSubmit.disabled = true; phoneSubmit.textContent = 'המשך'; }
    if (phoneError) {
      phoneError.setAttribute('hidden', '');
      phoneError.textContent = 'נא להזין מספר טלפון נייד תקין (10 ספרות שמתחילות ב-05)';
    }
    if (otpInput)  otpInput.value = '';
    if (otpSubmit) { otpSubmit.disabled = true; otpSubmit.textContent = 'אישור'; }
    if (otpError) { otpError.setAttribute('hidden', ''); otpError.textContent = ''; }
    if (resendBtn) { resendBtn.disabled = false; resendBtn.textContent = 'לא קיבלת SMS? שלח בשנית'; }
    if (resendStatus) { resendStatus.setAttribute('hidden', ''); resendStatus.textContent = ''; }
    if (customersList) customersList.replaceChildren();
    if (customersError) { customersError.setAttribute('hidden', ''); customersError.textContent = ''; }

    // Phase 3 fields
    const emailInput        = this.querySelector('#LoginDrawer-Email');
    const emailSubmit       = this.querySelector('[data-action="submit-email"]');
    const emailError        = this.querySelector('#LoginDrawer-EmailError');
    const firstNameInput    = this.querySelector('#LoginDrawer-FirstName');
    const lastNameInput     = this.querySelector('#LoginDrawer-LastName');
    const registerSubmit    = this.querySelector('[data-action="submit-register"]');
    const registerError     = this.querySelector('#LoginDrawer-RegisterError');
    const conflictError     = this.querySelector('[data-conflict-error]');
    const emailOtpInput     = this.querySelector('#LoginDrawer-EmailOtp');
    const emailOtpSubmit    = this.querySelector('[data-action="submit-email-otp"]');
    const emailOtpError     = this.querySelector('#LoginDrawer-EmailOtpError');
    const emailResendStatus = this.querySelector('[data-email-resend-status]');
    const emailResendBtn    = this.querySelector('[data-action="resend-email-otp"]');

    if (emailInput)        emailInput.value = '';
    if (emailSubmit)       { emailSubmit.disabled = true; emailSubmit.textContent = 'המשך'; }
    if (emailError)        { emailError.setAttribute('hidden', ''); emailError.textContent = ''; }
    if (firstNameInput)    firstNameInput.value = '';
    if (lastNameInput)     lastNameInput.value = '';
    if (registerSubmit)    { registerSubmit.disabled = true; registerSubmit.textContent = 'הרשם והתחבר'; }
    if (registerError)     { registerError.setAttribute('hidden', ''); registerError.textContent = ''; }
    if (conflictError)     { conflictError.setAttribute('hidden', ''); conflictError.textContent = ''; }
    if (emailOtpInput)     emailOtpInput.value = '';
    if (emailOtpSubmit)    { emailOtpSubmit.disabled = true; emailOtpSubmit.textContent = 'אמת קוד'; }
    if (emailOtpError)     { emailOtpError.setAttribute('hidden', ''); emailOtpError.textContent = ''; }
    if (emailResendStatus) { emailResendStatus.setAttribute('hidden', ''); emailResendStatus.textContent = ''; }
    if (emailResendBtn)    { emailResendBtn.disabled = false; emailResendBtn.textContent = 'שלח שוב'; }

    this.showStep('phone');
  }

  // ---------- HTTP helper ----------

  async _postJSON(url, body) {
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      let data = null;
      try { data = await res.json(); } catch (_) { /* non-JSON body */ }
      return { ok: res.ok, status: res.status, data, networkError: false };
    } catch (e) {
      console.warn('[login-drawer] network error', e);
      return { ok: false, status: 0, data: null, networkError: true };
    }
  }

  // Centralized handling for "token_expired" / "invalid_token": any endpoint
  // that uses the verification_token can return this. Reset and show error.
  _handleTokenExpired() {
    this._resetState();
    this._showPhoneError('פג תוקף הבקשה. נסה שוב מההתחלה.');
  }

  _isTokenError(result) {
    const err = result?.data?.error;
    return err === 'token_expired' || err === 'invalid_token' || err === 'token_already_used';
  }

  // ---------- Phone form ----------

  _wirePhoneForm() {
    const form = this.querySelector('[data-screen="phone"]');
    const input = this.querySelector('#LoginDrawer-Phone');
    const submit = this.querySelector('[data-action="submit-phone"]');
    if (!form || !input || !submit) return;

    input.addEventListener('input', () => {
      submit.disabled = !this._isValidPhone(input.value);
      const err = this.querySelector('#LoginDrawer-PhoneError');
      if (err) err.setAttribute('hidden', '');
    });

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const phone = input.value.trim();
      if (!this._isValidPhone(phone)) {
        this._showPhoneError('נא להזין מספר טלפון נייד תקין (10 ספרות שמתחילות ב-05)');
        return;
      }

      this._phone = phone;

      const display = this.querySelector('[data-phone-display]');
      if (display) display.textContent = phone;

      this._postJSON(ENDPOINTS.request, { phone }).then((r) => {
        if (!r.ok) this._handleRequestOtpFailure(r);
      });

      this._startResendCooldown(30);
      this.showStep('otp');
    });
  }

  _showPhoneError(message) {
    const err = this.querySelector('#LoginDrawer-PhoneError');
    if (err) {
      err.textContent = message;
      err.removeAttribute('hidden');
    }
  }

  _handleRequestOtpFailure(result) {
    this._stopResendCooldown();
    let message = 'אירעה שגיאה בשליחת SMS. נסה שוב.';
    if (result.networkError) {
      message = 'בעיית רשת. בדוק את החיבור ונסה שוב.';
    } else if (result.data?.error === 'rate_limited') {
      message = 'נשלחו יותר מדי בקשות. נסה שוב בעוד מספר דקות.';
    } else if (result.data?.error === 'sms_failed') {
      message = 'לא הצלחנו לשלוח SMS. נסה שוב בעוד רגע.';
    } else if (result.data?.error === 'invalid_phone' || result.data?.error === 'invalid_input') {
      message = 'מספר הטלפון לא תקין. נסה שוב.';
    }
    const otpInput = this.querySelector('#LoginDrawer-Otp');
    const otpSubmit = this.querySelector('[data-action="submit-otp"]');
    if (otpInput) otpInput.value = '';
    if (otpSubmit) { otpSubmit.disabled = true; otpSubmit.textContent = 'אישור'; }
    this.showStep('phone');
    this._showPhoneError(message);
  }

  _isValidPhone(value) {
    return /^05\d{8}$/.test(String(value || '').trim());
  }

  // ---------- OTP form ----------

  _wireOtpForm() {
    const form = this.querySelector('[data-screen="otp"]');
    const input = this.querySelector('#LoginDrawer-Otp');
    const submit = this.querySelector('[data-action="submit-otp"]');
    if (!form || !input || !submit) return;

    input.addEventListener('input', () => {
      const cleaned = input.value.replace(/\D/g, '').slice(0, 6);
      if (cleaned !== input.value) input.value = cleaned;
      submit.disabled = !this._isValidOtp(input.value);
      const err = this.querySelector('#LoginDrawer-OtpError');
      if (err) err.setAttribute('hidden', '');
    });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const code = input.value.trim();
      if (!this._isValidOtp(code)) return;

      submit.disabled = true;
      submit.textContent = 'טוען...';

      const r = await this._postJSON(ENDPOINTS.verify, { phone: this._phone, code });

      if (!r.ok) {
        this._handleVerifyFailure(r);
        submit.textContent = 'אישור';
        return;
      }

      this._verificationToken = r.data?.verification_token || '';
      this._customers = Array.isArray(r.data?.customers) ? r.data.customers : [];

      // Case A: zero customers → email entry (Phase 3 replaces the old register redirect)
      if (this._customers.length === 0) {
        this._emailScreenOrigin = 'phone';
        this.showStep('email');
        submit.textContent = 'אישור';
        submit.disabled = true;
        return;
      }

      // 1+ customers: show selection
      this.showCustomerList(this._customers);
      submit.textContent = 'אישור';
      submit.disabled = true;
    });
  }

  _handleVerifyFailure(result) {
    const err = this.querySelector('#LoginDrawer-OtpError');
    const input = this.querySelector('#LoginDrawer-Otp');
    let message = 'אירעה שגיאה. נסה שוב.';
    if (result.networkError) {
      message = 'בעיית רשת. נסה שוב.';
    } else if (result.data?.error === 'invalid_code') {
      message = 'קוד שגוי, נסה שוב';
    } else if (result.data?.error === 'too_many_attempts') {
      message = 'יותר מדי ניסיונות. שלח SMS חדש.';
    } else if (result.data?.error === 'otp_expired') {
      message = 'תוקף הקוד פג. שלח SMS חדש.';
    } else if (result.data?.error === 'no_active_otp') {
      message = 'לא נמצאה בקשת אימות פעילה. שלח SMS חדש.';
    }
    if (err) { err.textContent = message; err.removeAttribute('hidden'); }
    if (input) { input.value = ''; input.focus(); }
  }

  _isValidOtp(value) {
    return /^\d{6}$/.test(String(value || '').trim());
  }

  // ---------- Back link (OTP screen) ----------

  _wireBackButton() {
    const back = this.querySelector('[data-action="back"]');
    if (!back) return;
    back.addEventListener('click', () => {
      this._stopResendCooldown();
      const otpInput = this.querySelector('#LoginDrawer-Otp');
      const otpSubmit = this.querySelector('[data-action="submit-otp"]');
      const otpError = this.querySelector('#LoginDrawer-OtpError');
      const resendStatus = this.querySelector('[data-resend-status]');
      if (otpInput) otpInput.value = '';
      if (otpSubmit) { otpSubmit.disabled = true; otpSubmit.textContent = 'אישור'; }
      if (otpError) { otpError.setAttribute('hidden', ''); otpError.textContent = ''; }
      if (resendStatus) { resendStatus.setAttribute('hidden', ''); resendStatus.textContent = ''; }
      this.showStep('phone');
    });
  }

  // ---------- Resend OTP (phone) ----------

  _wireResendOtp() {
    const btn = this.querySelector('[data-action="resend-otp"]');
    if (!btn) return;
    btn.addEventListener('click', async () => {
      if (!this._phone) return;
      const status = this.querySelector('[data-resend-status]');
      const err = this.querySelector('#LoginDrawer-OtpError');
      const otpInput = this.querySelector('#LoginDrawer-Otp');
      const otpSubmit = this.querySelector('[data-action="submit-otp"]');

      this._startResendCooldown(30);
      if (err) err.setAttribute('hidden', '');
      if (status) { status.textContent = ''; status.setAttribute('hidden', ''); }
      if (otpInput) otpInput.value = '';
      if (otpSubmit) otpSubmit.disabled = true;

      const r = await this._postJSON(ENDPOINTS.request, { phone: this._phone });

      if (r.ok) {
        if (status) { status.textContent = 'נשלח קוד חדש'; status.removeAttribute('hidden'); }
        if (otpInput) otpInput.focus();
      } else {
        this._stopResendCooldown();
        let message = 'שליחה נכשלה. נסה שוב.';
        if (r.networkError) message = 'בעיית רשת. נסה שוב.';
        else if (r.data?.error === 'rate_limited') message = 'נשלחו יותר מדי בקשות. נסה שוב בעוד מספר דקות.';
        if (err) { err.textContent = message; err.removeAttribute('hidden'); }
      }
    });
  }

  // ---------- Customer selection ----------

  _wireCustomerSelection() {
    const list = this.querySelector('[data-customers-list]');
    if (!list) return;
    list.addEventListener('click', async (e) => {
      const target = e.target instanceof Element ? e.target : null;
      const btn = target ? target.closest('[data-customer-id]') : null;
      if (!btn) return;
      const customerId = btn.getAttribute('data-customer-id');
      if (!customerId) return;

      list.querySelectorAll('[data-customer-id]').forEach((el) => { el.disabled = true; });
      const noneBtn = this.querySelector('[data-action="none-of-these"]');
      if (noneBtn) noneBtn.disabled = true;
      const nameEl = btn.querySelector('.login-drawer__customer-name');
      const emailEl = btn.querySelector('.login-drawer__customer-email');
      if (nameEl) nameEl.textContent = 'מתחבר...';
      if (emailEl) emailEl.textContent = '';

      await this._completeLogin(customerId);
    });
  }

  showCustomerList(customers) {
    const list = this.querySelector('[data-customers-list]');
    const errEl = this.querySelector('[data-customers-error]');
    if (!list) return;

    list.replaceChildren();
    if (errEl) { errEl.setAttribute('hidden', ''); errEl.textContent = ''; }

    // Re-enable "none of these" if previously disabled
    const noneBtn = this.querySelector('[data-action="none-of-these"]');
    if (noneBtn) noneBtn.disabled = false;

    const isSingle = customers.length === 1;
    const heading = this.querySelector('#LoginDrawer-Heading');
    if (heading) {
      const attr = isSingle ? 'data-text-customers-single' : 'data-text-customers-multiple';
      const text = heading.getAttribute(attr);
      if (text) heading.textContent = text;
    }
    const desc = this.querySelector('[data-customers-description]');
    if (desc) {
      const attr = isSingle ? 'data-text-single' : 'data-text-multiple';
      const text = desc.getAttribute(attr);
      if (text) desc.textContent = text;
    }

    for (const c of customers) {
      const li = document.createElement('li');
      li.className = 'login-drawer__customer-item-wrapper';

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'login-drawer__customer-item';
      btn.setAttribute('data-customer-id', c.customer_id);

      const nameSpan = document.createElement('span');
      nameSpan.className = 'login-drawer__customer-name';
      const fullName = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
      nameSpan.textContent = fullName || 'חשבון ללא שם';

      const emailSpan = document.createElement('span');
      emailSpan.className = 'login-drawer__customer-email';
      emailSpan.setAttribute('dir', 'ltr');
      emailSpan.textContent = c.email || '';

      btn.append(nameSpan, emailSpan);
      li.append(btn);
      list.append(li);
    }

    this.showStep('customers');
  }

  // ---------- Complete login (existing customer) ----------

  async _completeLogin(customerId) {
    if (!this._verificationToken || !customerId) {
      this._handleCompleteFailure();
      return;
    }
    const r = await this._postJSON(ENDPOINTS.complete, {
      verification_token: this._verificationToken,
      customer_id: customerId,
    });
    if (!r.ok || !r.data?.multipass_url) {
      this._handleCompleteFailure(r);
      return;
    }
    this._redirectToMultipass(r.data.multipass_url);
  }

  _handleCompleteFailure(result) {
    if (result && this._isTokenError(result)) {
      this._handleTokenExpired();
      return;
    }
    this._resetState();
    this._showPhoneError('אירעה שגיאה, נסה שוב מההתחלה');
  }

  // ---------- "None of these" button (customers screen) ----------

  _wireNoneOfThese() {
    const btn = this.querySelector('[data-action="none-of-these"]');
    if (!btn) return;
    btn.addEventListener('click', () => {
      this._emailScreenOrigin = 'customers';
      this.showStep('email');
    });
  }

  // ---------- Email form ----------

  _wireEmailForm() {
    const form = this.querySelector('[data-screen="email"]');
    const input = this.querySelector('#LoginDrawer-Email');
    const submit = this.querySelector('[data-action="submit-email"]');
    if (!form || !input || !submit) return;

    input.addEventListener('input', () => {
      submit.disabled = !this._isValidEmail(input.value);
      const err = this.querySelector('#LoginDrawer-EmailError');
      if (err) err.setAttribute('hidden', '');
    });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = input.value.trim().toLowerCase();
      if (!this._isValidEmail(email)) {
        this._showEmailError('כתובת אימייל לא תקינה');
        return;
      }

      this._email = email;
      submit.disabled = true;
      submit.textContent = 'טוען...';

      const r = await this._postJSON(ENDPOINTS.submitEmail, {
        verification_token: this._verificationToken,
        email,
      });

      if (!r.ok) {
        if (this._isTokenError(r)) { this._handleTokenExpired(); return; }
        let message = 'אירעה שגיאה. נסה שוב.';
        if (r.networkError) message = 'בעיית רשת. נסה שוב.';
        else if (r.data?.error === 'invalid_email_format') message = 'כתובת אימייל לא תקינה';
        this._showEmailError(message);
        submit.textContent = 'המשך';
        submit.disabled = false;
        return;
      }

      if (r.data?.status === 'email_available') {
        this._populateRegisterScreen();
        this.showStep('register');
        submit.textContent = 'המשך';
        submit.disabled = true;
        return;
      }

      if (r.data?.status === 'email_taken') {
        this._existingPhoneMasked = r.data?.existing_phone_masked || '';
        this._populateEmailConflictScreen();
        this.showStep('email-conflict');
        submit.textContent = 'המשך';
        submit.disabled = true;
        return;
      }

      // Unexpected response shape
      this._showEmailError('אירעה שגיאה. נסה שוב.');
      submit.textContent = 'המשך';
      submit.disabled = false;
    });
  }

  _isValidEmail(value) {
    return EMAIL_REGEX.test(String(value || '').trim().toLowerCase());
  }

  _showEmailError(message) {
    const err = this.querySelector('#LoginDrawer-EmailError');
    if (err) { err.textContent = message; err.removeAttribute('hidden'); }
  }

  _populateRegisterScreen() {
    const fn = this.querySelector('#LoginDrawer-FirstName');
    const ln = this.querySelector('#LoginDrawer-LastName');
    const emailDisplay = this.querySelector('[data-register-email]');
    const phoneDisplay = this.querySelector('[data-register-phone]');
    const submit = this.querySelector('[data-action="submit-register"]');

    if (fn) fn.value = '';
    if (ln) ln.value = '';
    if (emailDisplay) emailDisplay.textContent = this._email;
    if (phoneDisplay) phoneDisplay.textContent = this._phone;
    if (submit) submit.disabled = !this._isRegisterValid();
  }

  _populateEmailConflictScreen() {
    const hint = this.querySelector('[data-conflict-phone]');
    const inline = this.querySelector('[data-conflict-phone-inline]');
    const otpPhone = this.querySelector('[data-conflict-otp-phone]');
    if (hint) hint.textContent = this._existingPhoneMasked || '';
    if (inline) inline.textContent = this._existingPhoneMasked || '';
    if (otpPhone) otpPhone.textContent = this._phone;
  }

  // ---------- Register form ----------

  _wireRegisterForm() {
    const form = this.querySelector('[data-screen="register"]');
    const fn = this.querySelector('#LoginDrawer-FirstName');
    const ln = this.querySelector('#LoginDrawer-LastName');
    const submit = this.querySelector('[data-action="submit-register"]');
    const errEl = this.querySelector('#LoginDrawer-RegisterError');
    if (!form || !fn || !ln || !submit) return;

    const onInput = () => {
      submit.disabled = !this._isRegisterValid();
      if (errEl) errEl.setAttribute('hidden', '');
    };
    fn.addEventListener('input', onInput);
    ln.addEventListener('input', onInput);

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!this._isRegisterValid()) return;

      submit.disabled = true;
      submit.textContent = 'טוען...';

      const r = await this._postJSON(ENDPOINTS.createCustomer, {
        verification_token: this._verificationToken,
        email: this._email,
        first_name: fn.value.trim(),
        last_name: ln.value.trim(),
      });

      if (!r.ok || !r.data?.multipass_url) {
        if (this._isTokenError(r)) { this._handleTokenExpired(); return; }
        let message = 'אירעה שגיאה ביצירת החשבון. נסה שוב.';
        if (r.networkError) message = 'בעיית רשת. נסה שוב.';
        else if (r.data?.error === 'email_now_taken') {
          message = 'המייל הזה נרשם בינתיים על ידי מישהו אחר. נסה מייל אחר.';
        }
        if (errEl) { errEl.textContent = message; errEl.removeAttribute('hidden'); }
        submit.textContent = 'הרשם והתחבר';
        submit.disabled = false;
        return;
      }

      this._redirectToMultipass(r.data.multipass_url);
    });
  }

  _isRegisterValid() {
    const fn = this.querySelector('#LoginDrawer-FirstName');
    const ln = this.querySelector('#LoginDrawer-LastName');
    return !!(fn?.value.trim() && ln?.value.trim());
  }

  // ---------- Email conflict screen ----------

  _wireEmailConflict() {
    const accept = this.querySelector('[data-action="conflict-accept"]');
    const reject = this.querySelector('[data-action="conflict-reject"]');
    if (accept) {
      accept.addEventListener('click', () => {
        // User says "this is my account: let me log in with the other phone".
        // Reset everything and go back to phone screen (per spec, don't close drawer).
        this._resetState();
      });
    }
    if (reject) {
      reject.addEventListener('click', async () => {
        // User wants to claim the email and attach it to their OTP phone.
        reject.disabled = true;
        if (accept) accept.disabled = true;
        const errEl = this.querySelector('[data-conflict-error]');
        if (errEl) errEl.setAttribute('hidden', '');

        const r = await this._postJSON(ENDPOINTS.sendEmailOtp, {
          verification_token: this._verificationToken,
        });

        // Always re-enable buttons so user can retry if needed
        reject.disabled = false;
        if (accept) accept.disabled = false;

        if (!r.ok) {
          if (this._isTokenError(r)) { this._handleTokenExpired(); return; }
          let message = 'אירעה שגיאה בשליחת הקוד. נסה שוב.';
          if (r.networkError) message = 'בעיית רשת. נסה שוב.';
          if (errEl) { errEl.textContent = message; errEl.removeAttribute('hidden'); }
          return;
        }

        // Show email OTP screen and auto-start the 30s resend cooldown
        const display = this.querySelector('[data-email-otp-display]');
        if (display) display.textContent = this._email;
        this.showStep('email-otp');
        this._startEmailResendCooldown(30);
      });
    }
  }

  // ---------- Email OTP form ----------

  _wireEmailOtpForm() {
    const form = this.querySelector('[data-screen="email-otp"]');
    const input = this.querySelector('#LoginDrawer-EmailOtp');
    const submit = this.querySelector('[data-action="submit-email-otp"]');
    if (!form || !input || !submit) return;

    input.addEventListener('input', () => {
      const cleaned = input.value.replace(/\D/g, '').slice(0, 6);
      if (cleaned !== input.value) input.value = cleaned;
      submit.disabled = !this._isValidOtp(input.value);
      const err = this.querySelector('#LoginDrawer-EmailOtpError');
      if (err) err.setAttribute('hidden', '');
    });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const code = input.value.trim();
      if (!this._isValidOtp(code)) return;

      submit.disabled = true;
      submit.textContent = 'טוען...';

      const r = await this._postJSON(ENDPOINTS.verifyEmailOtp, {
        verification_token: this._verificationToken,
        code,
      });

      if (r.ok && r.data?.multipass_url) {
        this._redirectToMultipass(r.data.multipass_url);
        return;
      }

      if (this._isTokenError(r)) { this._handleTokenExpired(); return; }

      const errEl = this.querySelector('#LoginDrawer-EmailOtpError');
      let message = 'אירעה שגיאה. נסה שוב.';
      if (r.networkError) {
        message = 'בעיית רשת. נסה שוב.';
      } else if (r.data?.error === 'invalid_code') {
        const left = r.data?.attempts_left ?? 0;
        message = left > 0 ? `קוד שגוי. נשארו ${left} ניסיונות` : 'קוד שגוי. חרגת ממספר הניסיונות.';
      } else if (r.data?.error === 'expired') {
        message = "פג תוקף הקוד. לחץ על 'שלח שוב'";
      } else if (r.data?.error === 'max_attempts') {
        message = 'חרגת ממספר הניסיונות. פנה לשירות הלקוחות';
      }
      if (errEl) { errEl.textContent = message; errEl.removeAttribute('hidden'); }
      if (input) { input.value = ''; input.focus(); }
      submit.textContent = 'אמת קוד';
      submit.disabled = true;
    });
  }

  // ---------- Resend email OTP ----------

  _wireEmailOtpResend() {
    const btn = this.querySelector('[data-action="resend-email-otp"]');
    if (!btn) return;
    btn.addEventListener('click', async () => {
      const status = this.querySelector('[data-email-resend-status]');
      const err = this.querySelector('#LoginDrawer-EmailOtpError');
      const input = this.querySelector('#LoginDrawer-EmailOtp');
      const submit = this.querySelector('[data-action="submit-email-otp"]');

      this._startEmailResendCooldown(30);
      if (err) err.setAttribute('hidden', '');
      if (status) { status.textContent = ''; status.setAttribute('hidden', ''); }
      if (input) input.value = '';
      if (submit) submit.disabled = true;

      const r = await this._postJSON(ENDPOINTS.sendEmailOtp, {
        verification_token: this._verificationToken,
      });

      if (r.ok) {
        if (status) { status.textContent = 'נשלח קוד חדש'; status.removeAttribute('hidden'); }
        if (input) input.focus();
      } else {
        this._stopEmailResendCooldown();
        if (this._isTokenError(r)) { this._handleTokenExpired(); return; }
        let message = 'שליחה נכשלה. נסה שוב.';
        if (r.networkError) message = 'בעיית רשת. נסה שוב.';
        if (err) { err.textContent = message; err.removeAttribute('hidden'); }
      }
    });
  }

  // ---------- Additional back buttons (email / register / etc.) ----------

  _wireExtraBackButtons() {
    const fromEmail = this.querySelector('[data-action="back-from-email"]');
    if (fromEmail) {
      fromEmail.addEventListener('click', () => {
        // Back from email goes to whichever screen brought us here.
        if (this._emailScreenOrigin === 'customers') {
          this.showStep('customers');
        } else {
          this._resetState();
        }
      });
    }

    const toEmail = this.querySelector('[data-action="back-to-email"]');
    if (toEmail) {
      toEmail.addEventListener('click', () => {
        // From register back to email: keep the entered email + token.
        this.showStep('email');
      });
    }

    // Back from the email-conflict screen → re-enter a different email.
    const fromConflict = this.querySelector('[data-action="back-from-conflict"]');
    if (fromConflict) {
      fromConflict.addEventListener('click', () => {
        const conflictError = this.querySelector('[data-conflict-error]');
        if (conflictError) { conflictError.setAttribute('hidden', ''); conflictError.textContent = ''; }
        this.showStep('email');
      });
    }

    // Back from the email-OTP screen → the previous (email-conflict) screen.
    const fromEmailOtp = this.querySelector('[data-action="back-from-email-otp"]');
    if (fromEmailOtp) {
      fromEmailOtp.addEventListener('click', () => {
        this._stopEmailResendCooldown();
        this.showStep('email-conflict');
      });
    }
  }

  // ---------- Success / redirect ----------

  _redirectToMultipass(url) {
    this.showSuccess('מתחבר...');
    setTimeout(() => {
      const currentPath = window.location.pathname + window.location.search;
      const separator = url.includes('?') ? '&' : '?';
      const finalUrl = `${url}${separator}return_to=${encodeURIComponent(currentPath)}`;
      window.location.href = finalUrl;
    }, SUCCESS_REDIRECT_DELAY_MS);
  }

  showSuccess(message) {
    const el = this.querySelector('[data-success-text]');
    if (el) {
      const fallback = el.getAttribute('data-text-default') || 'התחברת בהצלחה';
      el.textContent = message || fallback;
    }
    this.showStep('success');
  }
}

customElements.define('login-drawer', LoginDrawer);
